import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'

import { registerClient } from '@/api/user/sign-up'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useHeadManager } from '@/lib/headManagerProvider'

const signUpSchema = z
  .object({
    nome: z
      .string()
      .min(1, 'Nome obrigatório')
      .refine((value) => value.trim().split(/\s+/).length >= 2, {
        message: 'Digite pelo menos nome e sobrenome',
      }),
    email: z.string().email({ message: 'E-mail inválido' }),
    whatsapp: z
      .string()
      .min(14, 'Whatsapp inválido')
      .max(15, 'Whatsapp inválido')
      .regex(/^\(\d{2}\) \d{5}-\d{4}$/, 'Whatsapp inválido'),
    senha: z.string().min(6, 'Senha deve ter ao menos 6 caracteres'),
    confirmaSenha: z.string().min(6, 'Confirmação obrigatória'),
  })
  .refine((data) => data.senha === data.confirmaSenha, {
    message: 'As senhas não coincidem',
    path: ['confirmaSenha'],
  })

type SignUpForm = z.infer<typeof signUpSchema>

export function SignUp() {
  const { setTitle } = useHeadManager()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignUpForm>({
    resolver: zodResolver(signUpSchema),
  })

  const { mutateAsync: registerClientFn } = useMutation({
    mutationFn: registerClient,
    onSuccess: (data, variables) => {
      toast.success(data.message || 'Cadastro realizado com sucesso!', {
        action: {
          label: 'Ir para Login',
          onClick: () => navigate(`/sign-in?email=${variables.email}`),
        },
      })
      setTimeout(() => navigate(`/sign-in?email=${variables.email}`), 5000)
    },
    onError: (error: Error) => {
      if (error.message.includes('já está cadastrado')) {
        toast.error('E-mail já cadastrado', {
          description: error.message,
          action: {
            label: 'Fazer Login',
            onClick: () => navigate('/sign-in'),
          },
        })
      } else {
        toast.error('Erro no cadastro', {
          description:
            error.message || 'Ocorreu um erro ao realizar o cadastro',
        })
      }
    },
  })

  useEffect(() => {
    setTitle('Cadastro')
  }, [setTitle])

  async function handleSignUp(data: SignUpForm) {
    await registerClientFn({
      nome: data.nome,
      email: data.email,
      whatsapp: data.whatsapp,
      senha: data.senha,
    })
  }

  return (
    <div className="p-8">
      <Button variant="ghost" asChild className="absolute top-8 right-8">
        <Link to="/sign-in">Fazer login</Link>
      </Button>

      <div className="flex w-[350px] flex-col justify-center gap-6">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            Criar conta grátis
          </h1>
          <p className="text-muted-foreground text-sm">
            Faça parte do melhor site de rifas do Brasil!
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit(handleSignUp)}>
          <div className="space-y-2">
            <Label htmlFor="nome">Nome</Label>
            <Input
              id="nome"
              type="text"
              {...register('nome')}
              className={errors.nome ? 'border-red-500' : ''}
            />
            {errors.nome && (
              <p className="text-sm text-red-500">{errors.nome.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mail</Label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="whatsapp">Whatsapp</Label>
            <Input
              id="whatsapp"
              type="tel"
              maxLength={15}
              {...register('whatsapp', {
                onChange: (e) => {
                  const input = e.target.value
                  const onlyDigits = input.replace(/\D/g, '')
                  let formatted = ''
                  if (onlyDigits.length <= 10) {
                    formatted = onlyDigits
                      .replace(/^(\d{2})(\d{4})(\d{0,4})/, '($1) $2-$3')
                      .trim()
                  } else {
                    formatted = onlyDigits
                      .replace(/^(\d{2})(\d{5})(\d{0,4})/, '($1) $2-$3')
                      .trim()
                  }
                  e.target.value = formatted
                },
              })}
              className={errors.whatsapp ? 'border-red-500' : ''}
            />
            {errors.whatsapp && (
              <p className="text-sm text-red-500">{errors.whatsapp.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="senha">Senha</Label>
            <Input
              id="senha"
              type="password"
              {...register('senha')}
              className={errors.senha ? 'border-red-500' : ''}
            />
            {errors.senha && (
              <p className="text-sm text-red-500">{errors.senha.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmaSenha">Confirme sua senha</Label>
            <Input
              id="confirmaSenha"
              type="password"
              {...register('confirmaSenha')}
              className={errors.confirmaSenha ? 'border-red-500' : ''}
            />
            {errors.confirmaSenha && (
              <p className="text-sm text-red-500">
                {errors.confirmaSenha.message}
              </p>
            )}
          </div>

          <Button disabled={isSubmitting} className="w-full" type="submit">
            {isSubmitting ? 'Cadastrando...' : 'Finalizar cadastro'}
          </Button>

          <p className="text-muted-foreground px-6 text-center text-sm leading-relaxed">
            Ao continuar, você concorda com nossos{' '}
            <a href="" className="underline underline-offset-4">
              termos de serviço
            </a>{' '}
            e{' '}
            <a href="" className="underline underline-offset-4">
              políticas de privacidade
            </a>
          </p>
        </form>
      </div>
    </div>
  )
}
